import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { forkJoin } from 'rxjs';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { LocationRetailer } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { PosmSetupData } from '../models/posm-planning.model';
import { PosmService } from '../posm.service';

interface PosmSetupGroup {
  title: string;
  subtitle: string;
  icon: string;
  type: string;
  configHint?: string;
}

interface PosmSetupTab {
  id: string;
  title: string;
  icon: string;
  groups: PosmSetupGroup[];
}

@Component({
  selector: 'app-posm-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    TableModule,
    TabsModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './posm-setup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmSetupComponent implements OnInit {
  private posmService = inject(PosmService);
  private sessionStorageService = inject(SessionStorageService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private locationService = inject(LocationService);

  public readonly setupTabs: PosmSetupTab[] = [
    {
      id: 'project',
      title: 'Project Setup',
      icon: 'pi pi-briefcase',
      groups: [
        { title: 'Job Types', subtitle: 'Project and planner job categories', icon: 'pi pi-briefcase', type: 'JOB_TYPE' },
        { title: 'Project Statuses', subtitle: 'Reusable project lifecycle labels', icon: 'pi pi-flag', type: 'PROJECT_STATUS' },
        { title: 'Project Priorities', subtitle: 'Reusable project priority labels', icon: 'pi pi-bolt', type: 'JOB_PRIORITY' },
        { title: 'Task Types', subtitle: 'Store execution task types', icon: 'pi pi-list-check', type: 'TASK_TYPE' },
        { title: 'Incident Categories', subtitle: 'Issue categories used by POSM incidents', icon: 'pi pi-exclamation-triangle', type: 'INCIDENT_CATEGORY' },
        { title: 'Incident Severities', subtitle: 'Severity labels used by POSM incidents', icon: 'pi pi-bolt', type: 'INCIDENT_SEVERITY' },
        { title: 'Attachment Categories', subtitle: 'Project document and file categories', icon: 'pi pi-paperclip', type: 'ATTACHMENT_CATEGORY' },
        { title: 'Planner Import Templates', subtitle: 'Excel planner templates and mappings', icon: 'pi pi-file-excel', type: 'EXCEL_TEMPLATE', configHint: 'Use mappingJson for column mapping when needed.' }
      ]
    },
    {
      id: 'retailers',
      title: 'Retailers / Store Chains',
      icon: 'pi pi-building',
      groups: []
    },
    {
      id: 'strategy',
      title: 'Strategy Setup',
      icon: 'pi pi-sitemap',
      groups: [
        { title: 'Execution Strategies', subtitle: 'In-house, supplier, and hybrid execution paths', icon: 'pi pi-sitemap', type: 'EXECUTION_STRATEGY' },
        { title: 'Collection Preferences', subtitle: 'Material movement preferences for strategy work', icon: 'pi pi-truck', type: 'COLLECTION_PREFERENCE' }
      ]
    },
    {
      id: 'refurbishment',
      title: 'Refurbishment Workflow',
      icon: 'pi pi-wrench',
      groups: [
        { title: 'Refurbishment Stages', subtitle: 'Stock, refurbishment, and rebranding workflow states', icon: 'pi pi-wrench', type: 'REFURBISHMENT_STAGE' },
        { title: 'Refurbishment Actions', subtitle: 'Item-level refurbish, rebrand, production, and install actions', icon: 'pi pi-sync', type: 'REFURBISHMENT_ACTION' },
        { title: 'Manpower & Tools', subtitle: 'Reusable manpower and tool requirement options', icon: 'pi pi-users', type: 'MANPOWER_TOOL_OPTION', configHint: '{"defaultHeadcount":2,"type":"TEAM"}' }
      ]
    },
    {
      id: 'truck',
      title: 'Truck Alignment Templates',
      icon: 'pi pi-route',
      groups: [
        { title: 'Region / City Route Templates', subtitle: 'Reusable truck planning templates by region or city', icon: 'pi pi-route', type: 'ROUTE_TEMPLATE', configHint: '{"city":"Riyadh","objective":"MINIMIZE_TIME"}' },
        { title: 'Optimization Objectives', subtitle: 'Route optimization objective options', icon: 'pi pi-sliders-h', type: 'TRANSPORT_OBJECTIVE' }
      ]
    },
    {
      id: 'delivery',
      title: 'Delivery Notes',
      icon: 'pi pi-file',
      groups: [
        { title: 'Delivery Note Templates', subtitle: 'Delivery note layouts and required fields', icon: 'pi pi-file', type: 'DELIVERY_NOTE_TEMPLATE', configHint: '{"requiresStoreStamp":true,"requiresReceiverName":true}' }
      ]
    },
    {
      id: 'costing',
      title: 'Costing / Rates',
      icon: 'pi pi-wallet',
      groups: [
        { title: 'Own-Truck Rate Cards', subtitle: 'Internal fleet costing rules for POSM work', icon: 'pi pi-truck', type: 'OWN_TRUCK_RATE_CARD', configHint: '{"currency":"SAR","basis":"TRIP","rate":0}' },
        { title: 'Outside-Truck Rate Cards', subtitle: 'Outsourced truck costing rules for POSM work', icon: 'pi pi-send', type: 'OUTSIDE_TRUCK_RATE_CARD', configHint: '{"currency":"SAR","basis":"TRIP","rate":0}' }
      ]
    },
    {
      id: 'evidence',
      title: 'Evidence Fields',
      icon: 'pi pi-camera',
      groups: [
        { title: 'Evidence Templates', subtitle: 'Photo, signature, and proof fields for execution', icon: 'pi pi-camera', type: 'EVIDENCE_TEMPLATE', configHint: '{"fieldType":"PHOTO","required":true}' }
      ]
    },
    {
      id: 'approval',
      title: 'Approval Rules',
      icon: 'pi pi-shield',
      groups: [
        { title: 'Approval Rules', subtitle: 'Cost, supplier, and workflow approval rules', icon: 'pi pi-shield', type: 'APPROVAL_RULE', configHint: '{"sourceType":"THIRD_PARTY","approvalRequired":true}' }
      ]
    }
  ];

  public readonly setupTypes = Array.from(new Set(this.setupTabs.flatMap(tab => tab.groups.map(group => group.type))));
  public activeTab = signal(this.setupTabs[0].id);
  public setupRows = signal<Record<string, PosmSetupData[]>>({});
  public loading = signal(false);
  public drafts: Record<string, PosmSetupData> = {};
  public retailers = signal<LocationRetailer[]>([]);
  public retailerDraft = signal<LocationRetailer>({ name: '', active: true });

  public metrics = computed<PageMetric[]>(() => {
    const rows = Object.values(this.setupRows()).flat();
    return [
      {
        label: 'Setup Groups',
        value: this.setupTypes.length,
        hint: 'Configuration types available to admins',
        tone: 'info'
      },
      {
        label: 'Active Values',
        value: rows.filter(row => row.isActive !== false).length,
        hint: 'Values available in project screens',
        tone: 'success'
      },
      {
        label: 'Inactive Values',
        value: rows.filter(row => row.isActive === false).length,
        hint: 'Retained for history and reactivation',
        tone: 'default'
      },
      {
        label: 'Retailers',
        value: this.retailers().length,
        hint: 'Tenant-owned store chains',
        tone: 'info'
      }
    ];
  });

  constructor() {
    this.setupTypes.forEach(type => this.resetDraft(type));
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.activeTab.set(this.resolveTab(params.get('tab')));
    });
    this.load();
  }

  setActiveTab(value: string | number): void {
    const resolvedTab = this.resolveTab(String(value));
    this.activeTab.set(resolvedTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: resolvedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  load(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.loading.set(true);
    const requests = this.setupTypes.reduce((acc, type) => {
      acc[type] = this.posmService.getSetup(type, companyId, false);
      return acc;
    }, {} as Record<string, ReturnType<PosmService['getSetup']>>);

    forkJoin(requests).subscribe({
      next: rows => {
        this.setupRows.set(rows || {});
        this.setupTypes.forEach(type => {
          if (!this.drafts[type]?.id) {
            this.resetDraft(type);
          }
        });
        this.loadRetailers();
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'POSM Setup', detail: 'Unable to load POSM setup data.' });
      }
    });
  }

  loadRetailers(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.locationService.getRetailers(companyId, true).subscribe({
      next: response => this.retailers.set(response?.data || []),
      error: () => this.retailers.set([])
    });
  }

  saveRetailer(): void {
    const companyId = this.companyId();
    const draft = this.retailerDraft();
    const name = draft.name?.trim();
    if (!companyId || !name) {
      this.messageService.add({ severity: 'warn', summary: 'Retailer / Store Chain', detail: 'Name is required.' });
      return;
    }

    const payload: LocationRetailer = {
      ...draft,
      owningCompanyId: companyId,
      name,
      active: draft.active !== false
    };
    const request = payload.id
      ? this.locationService.updateRetailer(payload)
      : this.locationService.createRetailer(payload);
    request.subscribe({
      next: response => {
        const saved = response?.data;
        if (!saved) return;
        this.retailers.update(rows => this.mergeRetailers(rows, saved));
        this.resetRetailerDraft();
        this.messageService.add({ severity: 'success', summary: 'Retailer / Store Chain', detail: 'Retailer saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Retailer / Store Chain', detail: 'Unable to save retailer. Names must be unique for this owning company.' })
    });
  }

  editRetailer(row: LocationRetailer): void {
    this.retailerDraft.set({ ...row });
  }

  updateRetailerName(name: string): void {
    this.retailerDraft.update(current => ({ ...current, name }));
  }

  updateRetailerActive(active: boolean): void {
    this.retailerDraft.update(current => ({ ...current, active }));
  }

  toggleRetailer(row: LocationRetailer): void {
    this.locationService.updateRetailer({ ...row, active: row.active === false }).subscribe({
      next: response => {
        if (response?.data) this.retailers.update(rows => this.mergeRetailers(rows, response.data));
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Retailer / Store Chain', detail: 'Unable to update retailer.' })
    });
  }

  resetRetailerDraft(): void {
    this.retailerDraft.set({ owningCompanyId: this.companyId() || undefined, name: '', active: true });
  }

  seedDefaults(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.loading.set(true);
    this.posmService.seedPlanningDefaults(companyId).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'POSM Setup', detail: 'Standard setup values installed.' });
        this.load();
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'POSM Setup', detail: 'Unable to install standard setup values.' });
      }
    });
  }

  saveSetup(type: string): void {
    const companyId = this.companyId();
    const draft = this.drafts[type];
    const cleanName = draft?.name?.trim();
    if (!companyId || !cleanName) {
      this.messageService.add({ severity: 'warn', summary: 'POSM Setup', detail: 'Name is required.' });
      return;
    }
    if (!this.isValidJson(draft.configJson) || !this.isValidJson(draft.mappingJson)) {
      this.messageService.add({ severity: 'warn', summary: 'POSM Setup', detail: 'Configuration JSON must be valid.' });
      return;
    }

    const payload: PosmSetupData = {
      ...draft,
      owningCompanyId: companyId,
      type,
      name: cleanName,
      code: draft.code?.trim() || this.toCode(cleanName),
      category: draft.category?.trim() || this.defaultCategory(type),
      versionNumber: draft.versionNumber || 1,
      sortOrder: draft.sortOrder ?? this.rows(type).length + 1,
      isActive: draft.isActive !== false,
      isSystemDefault: draft.isSystemDefault === true
    };

    this.posmService.saveSetup(payload).subscribe({
      next: saved => {
        this.setupRows.update(current => ({
          ...current,
          [type]: this.mergeRows(current[type] || [], saved)
        }));
        this.resetDraft(type);
        this.messageService.add({ severity: 'success', summary: 'POSM Setup', detail: 'Setup value saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'POSM Setup', detail: 'Unable to save setup value.' })
    });
  }

  editSetup(row: PosmSetupData): void {
    this.drafts[row.type] = {
      ...row,
      effectiveFrom: row.effectiveFrom ? new Date(row.effectiveFrom) : undefined,
      effectiveTo: row.effectiveTo ? new Date(row.effectiveTo) : undefined
    };
  }

  toggleActive(row: PosmSetupData): void {
    this.posmService.saveSetup({
      ...row,
      owningCompanyId: row.owningCompanyId || this.companyId(),
      isActive: row.isActive === false
    }).subscribe({
      next: saved => {
        this.setupRows.update(current => ({
          ...current,
          [row.type]: this.mergeRows(current[row.type] || [], saved)
        }));
      }
    });
  }

  resetDraft(type: string): void {
    this.drafts[type] = {
      owningCompanyId: this.companyId() || undefined,
      type,
      name: '',
      code: '',
      category: this.defaultCategory(type),
      versionNumber: 1,
      sortOrder: (this.setupRows()[type]?.length || 0) + 1,
      isActive: true,
      isSystemDefault: false
    };
  }

  rows(type: string): PosmSetupData[] {
    return this.setupRows()[type] || [];
  }

  activeRows(type: string): number {
    return this.rows(type).filter(row => row.isActive !== false).length;
  }

  statusSeverity(row: PosmSetupData): string {
    return row.isActive === false ? 'secondary' : 'success';
  }

  private mergeRows(rows: PosmSetupData[], saved: PosmSetupData): PosmSetupData[] {
    const next = rows.some(row => row.id === saved.id)
      ? rows.map(row => row.id === saved.id ? saved : row)
      : [...rows, saved];
    return next.sort((a, b) => (a.sortOrder ?? 999999) - (b.sortOrder ?? 999999) || (a.name || '').localeCompare(b.name || ''));
  }

  private mergeRetailers(rows: LocationRetailer[], saved: LocationRetailer): LocationRetailer[] {
    const next = rows.some(row => row.id === saved.id)
      ? rows.map(row => row.id === saved.id ? saved : row)
      : [...rows, saved];
    return next.sort((a, b) => `${a.name || ''}`.localeCompare(`${b.name || ''}`));
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id || 0;
  }

  private defaultCategory(type: string): string {
    if (['JOB_TYPE', 'PROJECT_STATUS', 'JOB_PRIORITY', 'TASK_TYPE', 'INCIDENT_CATEGORY', 'INCIDENT_SEVERITY', 'ATTACHMENT_CATEGORY', 'EXCEL_TEMPLATE'].includes(type)) return 'Project';
    if (['EXECUTION_STRATEGY', 'COLLECTION_PREFERENCE'].includes(type)) return 'Strategy';
    if (['REFURBISHMENT_STAGE', 'REFURBISHMENT_ACTION', 'MANPOWER_TOOL_OPTION'].includes(type)) return 'Refurbishment';
    if (['ROUTE_TEMPLATE', 'TRANSPORT_OBJECTIVE'].includes(type)) return 'Routing';
    if (type === 'DELIVERY_NOTE_TEMPLATE') return 'Delivery Notes';
    if (type === 'EVIDENCE_TEMPLATE') return 'Evidence';
    if (type === 'APPROVAL_RULE') return 'Approvals';
    if (['OWN_TRUCK_RATE_CARD', 'OUTSIDE_TRUCK_RATE_CARD'].includes(type)) return 'Rates';
    return 'General';
  }

  private isValidJson(value?: string): boolean {
    if (!value?.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  private toCode(value: string): string {
    return value.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  private resolveTab(tab: string | null): string {
    return this.setupTabs.find(item => item.id === tab)?.id || this.setupTabs[0].id;
  }
}
